import axios from 'axios';
import { go } from './navigationService';
import ApiStorage from '../redux/ApiStorage';
import UtilNotify from './NotificationService';
import UtilNumber from './UtilNumber';
import { URL_REFRESH_TOKEN } from '../services/AuthActions';
import { getLocalizedPath } from '../i18nLanguages';
import i18n from '../i18n';

let BASE_URL = process.env.REACT_APP_BASE_NODE_URL;
const UPLOAD_TIMEOUT_MS = 600000;

export const getBaseURL = () => {
  return `${BASE_URL}/api/v1/`;
};

export const getBaseURLShowImage = (src) => {
  if (!src) return '';
  if (
    src.startsWith('http://') ||
    src.startsWith('https://') ||
    src.startsWith('blob:') ||
    src.startsWith('data:')
  ) {
    return src;
  }
  const cleanSrc = src.replace(/^\/+/, '');
  if (cleanSrc.startsWith('public/uploads/')) {
    const nodeBase = (
      process.env.REACT_APP_BASE_NODE_URL ||
      BASE_URL ||
      'http://localhost:5016'
    ).replace(/\/+$/, '');
    return `${nodeBase}/${cleanSrc}`;
  }
  const base = (
    process.env.REACT_APP_DO_SPACES_READ_URL ||
    process.env.REACT_APP_BASE_NODE_URL ||
    BASE_URL ||
    'http://localhost:5016'
  ).replace(/\/+$/, '');

  return `${base}/${cleanSrc}`;
};

export const getBaseURLShowFile = (src) => {
  return getBaseURLShowImage(src);
};

const adapter = axios.create({
  baseURL: getBaseURL(),
  headers: {
    Accept: 'application/json;charset=UTF-8',
    'Content-Type': 'application/json',
  },
  validateStatus: false,
  withCredentials: true,
  timeout: 15000,
});

const refreshAdapter = axios.create({
  baseURL: getBaseURL(),
  headers: {
    Accept: 'application/json;charset=UTF-8',
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  validateStatus: false,
  timeout: 15000,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(true);
    }
  });
  failedQueue = [];
};

const redirectToLogin = (errMsg) => {
  return new Promise(() => {
    const currentPath = window.location.pathname;
    UtilNotify.notifyErrorServer(errMsg || 'WORD_SESSION_EXPIRED_LOGIN_AGAIN');
    ApiStorage.clearUser();
    go(getLocalizedPath('/login', currentPath), {
      state: { force: true, redirectTo: currentPath },
    });
  });
};

export const handleRefreshToken = () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  return (async () => {
    try {
      let deviceKey = ApiStorage.getDeviceKey();
      if (!deviceKey) {
        deviceKey = UtilNumber.getNewUniqID();
        ApiStorage.setDeviceKey(deviceKey);
      }

      const refreshData = {
        device: 'admin',
        offset: getTimeZone(),
        deviceKey: deviceKey?.toString(),
      };

      const result = await refreshAdapter.post(URL_REFRESH_TOKEN, refreshData);

      if (result.status === 200 && result.data.success) {
        const currentUser = ApiStorage.getCurrentUser();
        if (currentUser) {
          ApiStorage.setCurrentUser({ ...currentUser, accessToken: result.data.data });
        }
        processQueue(null);
        return true;
      } else {
        processQueue(new Error(i18n.t('WORD_REFRESH_FAILED')));
        return false;
      }
    } catch (error) {
      processQueue(error);
      return false;
    } finally {
      isRefreshing = false;
    }
  })();
};

if (adapter._refreshInterceptorId !== undefined) {
  adapter.interceptors.response.eject(adapter._refreshInterceptorId);
}

adapter._refreshInterceptorId = adapter.interceptors.response.use(
  async (response) => {
    const { status, config } = response;

    if (status === 401 || status === 410) {
      if (
        config.url.includes('/users/auth/refresh_token') ||
        config.url.includes('/users/auth/logout') ||
        config.url.includes('/users/auth/login')
      ) {
        return response;
      }

      if (!config._retry) {
        config._retry = true;
        const success = await handleRefreshToken();
        if (success) {
          return adapter(config);
        } else {
          return redirectToLogin(response.data?.errMsg);
        }
      }
    }

    return response;
  },
  async (error) => {
    const { response, config } = error;
    if (response) {
      const { status } = response;
      if ((status === 401 || status === 410) && config && !config._retry) {
        config._retry = true;
        const success = await handleRefreshToken();
        if (success) {
          return adapter(config);
        } else {
          return redirectToLogin(response.data?.errMsg);
        }
      }
    }
    return Promise.reject(error);
  }
);

export async function fetchFromUrlPOSTAsync(
  url,
  parameters = [],
  adapterOptions = {}
) {
  return await fetchFromUrlPOSTAsyncMain(url, parameters, adapterOptions);
}

export async function fetchFromUrlPOSTAsyncLong(
  url,
  parameters = [],
  adapterOptions = {}
) {
  return await fetchFromUrlPOSTAsyncMain(url, parameters, {
    timeout: 600000,
    ...adapterOptions,
  });
}

export async function fetchFromUrlPOSTAsyncDownloadFile(
  url,
  parameters = [],
  fileName
) {
  let response = await fetchFromUrlPOSTAsyncMain(url, parameters, {
    responseType: 'blob',
  });
  const blob = new Blob([response], { encoding: 'UTF-8' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName + '.tsv';
  link.click();
  return response;
}

export async function fetchFromUrlPOSTAsyncMain(
  url,
  parameters = [],
  adapterOptions
) {
  try {
    let deviceKey = ApiStorage.getDeviceKey();
    if (!deviceKey) {
      deviceKey = UtilNumber.getNewUniqID();
      ApiStorage.setDeviceKey(deviceKey);
    }

    const dataPost = {
      device: 'admin',
      offset: getTimeZone(),
      deviceKey: deviceKey?.toString(),
    };

    if (Array.isArray(parameters)) {
      parameters.forEach((par) => {
        dataPost[par.key] = par.value;
      });
    } else {
      Object.assign(dataPost, parameters);
    }

    const result = await adapter.post(url, dataPost, adapterOptions);
    return result.data;
  } catch (error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      throw error;
    }
    console.warn('error = ', error);
    UtilNotify.notifyErrorServer(error.message);
    return { success: false, errMsg: error.message };
  }
}

export async function fetchFromUrlGETAsync(
  url,
  parameters = [],
  adapterOptions = {}
) {
  try {
    let dataPost = {
      device: 'web',
      offset: getTimeZone(),
    };

    parameters.forEach((par) => {
      dataPost[par.key] = par.value;
    });

    let result = await adapter.get(url, {
      params: dataPost,
      ...adapterOptions,
    });
    return result.data;
  } catch (error) {
    console.warn('error = ', error);
    return { success: false, errMsg: error.message };
  }
}

export function getTimeZone() {
  return -new Date().getTimezoneOffset();
}

export async function fetchFromUrlPOSTAsyncUploadFile(
  url,
  file = [],
  adapterOptions = {},
  fileFieldName = 'image'
) {
  try {
    let deviceKey = ApiStorage.getDeviceKey();
    if (!deviceKey) {
      deviceKey = UtilNumber.getNewUniqID();
      ApiStorage.setDeviceKey(deviceKey);
    }

    const dataPost = new FormData();
    dataPost.append('device', 'admin');
    dataPost.append('offset', getTimeZone());
    dataPost.append('deviceKey', deviceKey.toString());
    dataPost.append(fileFieldName, file);

    const response = await adapter.post(url, dataPost, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: UPLOAD_TIMEOUT_MS,
      ...adapterOptions,
    });
    return response.data;
  } catch (error) {
    UtilNotify.notifyErrorServer(error.message);
    // history.push("/en/page_not_found", { force: true })
    // window.location.reload();
    return { success: false, errMsg: error.message };
  }

  // return await fetchFromUrlPOSTAsyncMain(url, parameters, { timeout: 600000 });
}

export default adapter;
